import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, User } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { Marquee } from "@/components/Marquee";
import { SocialLinks } from "@/components/Social";

export const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2 group">
    <span className="grid place-items-center h-10 w-10 rounded-full bg-sand border border-navy/15 font-display text-sm group-hover:rotate-12 transition-transform">GF</span>
    <span className="font-display text-lg sm:text-xl leading-none uppercase">Grandpa Finn's</span>
  </Link>
);

const CartButton = () => {
  const { count: qty, setCartOpen, anyActive } = useStore();
  if (!anyActive) return <span className="h-11 w-11" aria-hidden="true" />;
  return (
    <button data-testid="cart-drawer-toggle" onClick={() => setCartOpen(true)} aria-label="Cart"
      className="relative grid place-items-center h-11 w-11 rounded-full bg-aqua border border-navy/15 shadow-md hover:-translate-y-0.5 transition-transform">
      <ShoppingBag className="h-5 w-5" />
      {qty > 0 && (
        <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 h-6 min-w-6 px-1 rounded-full bg-coral text-white text-xs font-bold grid place-items-center border border-navy/15">{qty}</span>
      )}
    </button>
  );
};

const MobileNav = () => {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="mobile-menu-button" aria-label="Menu" className="lg:hidden grid place-items-center h-11 w-11 rounded-full border border-navy/15 bg-white">
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="bg-navy text-cream border-r border-navy/10 w-[85vw] max-w-sm">
        <SheetTitle className="font-display text-sunny text-2xl uppercase">Menu, kid</SheetTitle>
        <nav className="mt-8 flex flex-col gap-4">
          {[{ to: "/", label: "Home" }, ...NAV].map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="font-display text-3xl uppercase hover:text-aqua transition-colors">{n.label}</Link>
          ))}
        </nav>
        <SocialLinks prefix="mobile-nav" className="mt-10" />
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => (
  <header className="sticky top-0 z-40">
    <Marquee className="bg-navy text-sand/80 py-1.5 text-xs" />
    <div className="backdrop-blur-md bg-cream/85 border-b border-navy/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3"><MobileNav /><Logo /></div>
        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
              className={({ isActive }) => `font-semibold text-sm uppercase tracking-wide transition-colors hover:text-coral ${isActive ? "text-coral" : ""}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/account" data-testid="nav-account-link" aria-label="Account" className="grid place-items-center h-11 w-11 rounded-full bg-white border border-navy/15 hover:-translate-y-0.5 transition-transform"><User className="h-5 w-5" /></Link>
          <CartButton />
        </div>
      </div>
    </div>
  </header>
);
